import * as THREE from 'three'
import type { DepthFrame } from '../depth/depth-frame'
import { DepthFilter, DEFAULT_FILTER_MODE } from '../depth/filter'
import { buildRayTable, type RayTable } from '../depth/unproject'

/**
 * The sensed geometry: one depth frame unprojected into world-space cells, and
 * the triangle mesh built from them.
 *
 * The mesh writes depth but no colour, so real objects occlude the virtual
 * boxes. Without that the boxes draw over the real world and the whole thing
 * reads as floating sprites.
 *
 * The cell buffers are preallocated once per grid size and rewritten in place
 * every frame. At 30 fps over ~3,600 cells, reallocating them per frame is the
 * wrong trade; this is a measured decision, not an oversight. Every loop below
 * is bounded by the buffers' own dimensions, so indexed reads are asserted
 * rather than guarded.
 */

/** Every other depth pixel in each axis: 160x90 becomes an 80x45 cell grid. */
const STRIDE = 2

/**
 * Quads whose four corners disagree by more than this are a depth discontinuity
 * — the silhouette edge of an object against whatever is behind it — and
 * meshing them stretches a skirt across the gap. The rate term widens the
 * tolerance with distance, because the same physical step subtends less depth
 * difference further away.
 */
const EDGE_TOLERANCE = 0.08
const EDGE_TOLERANCE_RATE = 0.02

export class Occluder {
  private readonly geometry = new THREE.BufferGeometry()
  private readonly material: THREE.MeshBasicMaterial
  private readonly mesh: THREE.Mesh
  private readonly filter = new DepthFilter()

  private rays: RayTable | null = null
  private positionAttribute: THREE.BufferAttribute | null = null
  private colourAttribute: THREE.BufferAttribute | null = null
  private indexAttribute: THREE.BufferAttribute | null = null

  /** World XYZ per cell, three floats each. */
  positions = new Float32Array(0)
  /** Depth in metres per cell, 0 for unknown. */
  metres = new Float32Array(0)
  /** Vertex colours, written by the skin. */
  colours = new Float32Array(0)
  /** Scratch flags for the fitted-floor path. */
  flags = new Uint8Array(0)

  cells = 0
  /** Cells the sensor gave no depth for, read before anything downstream zeroes one. */
  emptyCells = 0

  private indices = new Uint32Array(0)

  constructor(scene: THREE.Scene) {
    // DoubleSide because the grid's winding depends on the sign of
    // `normDepthBufferFromNormView`, which is device- and orientation-dependent.
    this.material = new THREE.MeshBasicMaterial({
      colorWrite: false,
      depthWrite: true,
      side: THREE.DoubleSide,
    })
    this.mesh = new THREE.Mesh(this.geometry, this.material)
    this.mesh.frustumCulled = false
    this.mesh.renderOrder = -1
    scene.add(this.mesh)
  }

  /** Shared with the skin, which draws the same triangles with colour. */
  get sharedGeometry(): THREE.BufferGeometry {
    return this.geometry
  }

  get gridWidth(): number {
    return this.rays?.gridWidth ?? 0
  }

  get gridHeight(): number {
    return this.rays?.gridHeight ?? 0
  }

  get rayDirections(): Float32Array | null {
    return this.rays?.dirs ?? null
  }

  /** Rebuilds the world-space cell grid from one depth frame. */
  unproject(depth: DepthFrame): void {
    this.rays = buildRayTable(
      depth.view.projectionMatrix,
      depth.uvTransform,
      depth.width,
      depth.height,
      STRIDE,
      this.rays
    )

    const { dirs, gridWidth, gridHeight } = this.rays
    this.ensureBuffers(gridWidth * gridHeight)

    const { positions, metres } = this
    const world = depth.view.transform.matrix
    const source = this.filter.apply(
      depth.raw,
      depth.width,
      depth.height,
      depth.rawValueToMeters,
      DEFAULT_FILTER_MODE
    )

    let empty = 0

    for (let j = 0; j < gridHeight; j += 1) {
      const row = j * STRIDE * depth.width
      for (let i = 0; i < gridWidth; i += 1) {
        const cell = j * gridWidth + i
        const z = source[row + i * STRIDE]!
        metres[cell] = z
        if (z === 0) {
          empty += 1
          continue
        }

        const offset = cell * 3
        const vx = dirs[offset]! * z
        const vy = dirs[offset + 1]! * z
        const vz = -z

        positions[offset] = world[0]! * vx + world[4]! * vy + world[8]! * vz + world[12]!
        positions[offset + 1] = world[1]! * vx + world[5]! * vy + world[9]! * vz + world[13]!
        positions[offset + 2] = world[2]! * vx + world[6]! * vy + world[10]! * vz + world[14]!
      }
    }

    // Read before anything downstream zeroes a cell, so this stays a property
    // of the sensor. It has been 0 in every reading so far — ARCore's model
    // inpaints every pixel and never admits ignorance.
    this.emptyCells = empty
  }

  /** Re-emits the triangle index buffer from the current cell heights. */
  rebuild(): void {
    if (!this.rays || !this.indexAttribute || !this.positionAttribute) return
    const { gridWidth, gridHeight } = this.rays
    const { metres, indices } = this

    let count = 0
    for (let j = 0; j < gridHeight - 1; j += 1) {
      for (let i = 0; i < gridWidth - 1; i += 1) {
        const a = j * gridWidth + i
        const b = a + 1
        const c = a + gridWidth
        const d = c + 1

        const za = metres[a]!
        const zb = metres[b]!
        const zc = metres[c]!
        const zd = metres[d]!
        if (za === 0 || zb === 0 || zc === 0 || zd === 0) continue

        const lo = Math.min(za, zb, zc, zd)
        const hi = Math.max(za, zb, zc, zd)
        if (hi - lo > EDGE_TOLERANCE + lo * EDGE_TOLERANCE_RATE) continue

        indices[count] = a
        indices[count + 1] = c
        indices[count + 2] = b
        indices[count + 3] = b
        indices[count + 4] = c
        indices[count + 5] = d
        count += 6
      }
    }

    this.positionAttribute.needsUpdate = true
    this.indexAttribute.needsUpdate = true
    this.geometry.setDrawRange(0, count)
  }

  markColoursDirty(): void {
    if (this.colourAttribute) this.colourAttribute.needsUpdate = true
  }

  clear(): void {
    this.geometry.setDrawRange(0, 0)
    this.filter.reset()
    this.emptyCells = 0
  }

  dispose(): void {
    this.mesh.removeFromParent()
    this.material.dispose()
    this.geometry.dispose()
  }

  private ensureBuffers(cells: number): void {
    if (this.cells === cells) return

    this.cells = cells
    this.positions = new Float32Array(cells * 3)
    this.metres = new Float32Array(cells)
    this.colours = new Float32Array(cells * 3)
    this.flags = new Uint8Array(cells)
    this.indices = new Uint32Array(cells * 6)

    this.geometry.deleteAttribute('position')
    this.geometry.deleteAttribute('color')

    this.positionAttribute = new THREE.BufferAttribute(this.positions, 3)
    this.positionAttribute.setUsage(THREE.DynamicDrawUsage)
    this.colourAttribute = new THREE.BufferAttribute(this.colours, 3)
    this.colourAttribute.setUsage(THREE.DynamicDrawUsage)
    this.indexAttribute = new THREE.BufferAttribute(this.indices, 1)
    this.indexAttribute.setUsage(THREE.DynamicDrawUsage)

    this.geometry.setAttribute('position', this.positionAttribute)
    this.geometry.setAttribute('color', this.colourAttribute)
    this.geometry.setIndex(this.indexAttribute)
    this.geometry.setDrawRange(0, 0)
  }
}
